import { auth, completeEmailLoginIfPresent } from "@/lib/firebase"
import { cn } from "@/lib/utils"
import { Outlet } from "@tanstack/react-router"
import { getRedirectResult, signInWithCustomToken } from "firebase/auth"
import { useEffect, useMemo, useRef, useState } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { Login } from "./login"
import { useToast } from "@/components/ui/use-toast"
import { NavMenu } from "@/components/nav-menu"
import { PillarsBetaLogo } from "@/components/pillars-beta-logo.tsx"
import { Button } from "@/components/ui/button"
import { Menu, X } from "lucide-react"
import { client, validateClientProperty } from "@/lib/ts-rest.ts"
import { useMediaQuery } from "react-responsive"
import { identifyUser, captureError, trackEvent } from "@/lib/log"
import { DashboardContext } from "@/lib/dashboard-context"

export function Dashboard() {
  const { toast } = useToast()
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" })

  const [firebaseRawUser, firebaseRawLoading, firebaseRawError] =
    useAuthState(auth)

  const {
    data: authData,
    refetch: refetchAuthData,
    isPending: isPendingAuthData,
    isLoading: isLoadingAuthData,
    isError: isErrorAuthData,
  } = client.getAuthData.useQuery({
    queryKey: [validateClientProperty("getAuthData")],
    staleTime: Infinity,
    enabled: false,
  })

  const user = authData?.body.user

  useEffect(() => {
    completeEmailLoginIfPresent().then((result) => {
      if (result.status === "error") {
        toast({
          title: "Couldn't sign you in",
          description: "Your sign-in link may have expired. Please request a new one.",
          variant: "destructive",
        })
      } else if (result.status === "success") {
        trackEvent("signed in with email link")
      }
    })
  }, [toast])

  useEffect(() => {
    if (firebaseRawUser && !user) {
      refetchAuthData()
    }
  }, [firebaseRawUser, refetchAuthData, user])

  const identifiedRef = useRef(false)
  useEffect(() => {
    if (user && !identifiedRef.current) {
      identifiedRef.current = true
      identifyUser({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      })
      trackEvent("signed in")
    }
  }, [user])

  useEffect(() => {
    if (isErrorAuthData) {
      setTimeout(() => {
        refetchAuthData()
      }, 5000)
    }
  }, [isErrorAuthData, refetchAuthData])

  const firstRenderForRedirectResult = useRef(true)
  useEffect(() => {
    if (firstRenderForRedirectResult.current === true) {
      const run = async () => {
        try {
          await getRedirectResult(auth)
        } catch (error) {
          captureError(error, { tags: { source: "firebase_redirect" } })
        }
      }
      run()
      firstRenderForRedirectResult.current = false
    }
  }, [])

  const tokenLoginAttempted = useRef(false)
  useEffect(() => {
    if (tokenLoginAttempted.current) return
    const params = new URLSearchParams(window.location.search)
    const customToken = params.get("token")
    if (customToken) {
      tokenLoginAttempted.current = true
      signInWithCustomToken(auth, customToken)
        .then(() => {
          params.delete("token")
          const newUrl =
            window.location.pathname +
            (params.toString() ? `?${params.toString()}` : "")
          window.history.replaceState({}, "", newUrl)
        })
        .catch((error) => {
          captureError(error, { tags: { source: "custom_token_login" } })
        })
    }
  }, [])

  useEffect(() => {
    if (firebaseRawError) {
      toast({ variant: "destructive", title: firebaseRawError.message })
    }
  }, [firebaseRawError, toast])

  let showLoader = false
  if (firebaseRawLoading) {
    showLoader = true
  }
  if (isLoadingAuthData) {
    showLoader = true
  }

  const Loader = () => {
    return (
      <div
        className={cn(
          "z-10 fixed top-0 left-0 w-screen h-screen transition-opacity ease-in duration-1000 flex justify-center items-center bg-primary",
          showLoader && "opacity-100",
          !showLoader && "opacity-0 pointer-events-none"
        )}
      >
        <div className="p-5 bg-primary rounded-lg">
          <img src="/logo.png" className="h-10 w-10 animate-spin" />
        </div>
      </div>
    )
  }

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [flushContent, setFlushContent] = useState(false)
  const dashboardCtx = useMemo(() => ({ setFlushContent }), [])

  return (
    <>
      <Loader />
      {!user && <Login />}
      {user && (
        <DashboardContext.Provider value={dashboardCtx}>
          {!isMobile && (
            <div className="flex h-screen">
              <div className="w-72 p-14">
                <NavMenu />
              </div>
              <div className="flex-grow md:p-14 p-5 bg-light-primary overflow-auto">
                <Outlet />
              </div>
            </div>
          )}
          {isMobile && (
            <div className="h-[100dvh] flex flex-col bg-light-primary">
              <div className="p-5 flex justify-between bg-white shrink-0 shadow-sm relative z-10">
                <PillarsBetaLogo />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  {isMenuOpen ? (
                    <X className="h-6 w-6 text-red-600" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </Button>
              </div>
              <div className="relative flex-1 min-h-0 flex flex-col">
                <div
                  className={cn(
                    "border-y p-5 bg-white shadow-md opacity-0 absolute top-0 left-0 w-full h-auto transition-opacity ease-in duration-75 pointer-events-none z-10",
                    isMenuOpen && "opacity-100 pointer-events-auto"
                  )}
                >
                  <NavMenu
                    showLogo={false}
                    onClose={() => setIsMenuOpen(false)}
                  />
                </div>
                <div className={cn("md:p-14 flex-1 min-h-0 overflow-auto", !flushContent && "p-5")}>
                  <Outlet />
                </div>
              </div>
            </div>
          )}
        </DashboardContext.Provider>
      )}
    </>
  )
}
